import type {
  CustomerNotification,
  CustomerNotificationMetadata,
} from '@kizen/api/customer-notifications';
import type { TypographyProps } from '@kizen/kds/Typography';
import type { CSSProperties } from 'react';
import type { StatusColorClass } from './colors';

import styled from '@emotion/styled';
import { Typography } from '@kizen/kds/Typography';
import { useTranslation } from 'react-i18next';
import { SizedCell } from '__components/Kizen/Table';
import { useNotificationsPage } from './context';

type StatusProps = {
  className?: string;
  notification: CustomerNotification;
  style?: CSSProperties;
  weight?: TypographyProps['weight'];
};

const getStatus = (data: {
  metadata: CustomerNotificationMetadata;
  notification: CustomerNotification;
  t: (x: string) => string;
}): { label: string; color: StatusColorClass } => {
  const { notification, metadata, t } = data;

  if (
    metadata.completed_notification_types.includes(
      notification.notification_type
    )
  ) {
    return {
      color: 'bg-[var(--color-feedback-toast-information)]',
      label: t('Completed'),
    };
  }

  if (notification.is_in_progress) {
    return {
      color: 'bg-[var(--color-feedback-toast-success)]',
      label: t('In Progress'),
    };
  }

  if (notification.notification_class === 'error') {
    return {
      color: 'bg-[var(--color-feedback-toast-error)]',
      label: t('Error'),
    };
  }

  if (notification.notification_class === 'warning') {
    return {
      color: 'bg-[var(--color-feedback-toast-warning)]',
      label: t('Warning'),
    };
  }

  return {
    color: 'bg-[var(--color-feedback-toast-information)]',
    label: t('Info'),
  };
};

const Cell = styled(SizedCell)`
  > div {
    position: relative;
  }
`;

export const NotificationStatusCell = (props: StatusProps) => {
  const { className, notification, style, weight } = props;
  const { t } = useTranslation();
  const { metadata } = useNotificationsPage();
  const { table } = metadata;
  const { color, label } = getStatus({ metadata, notification, t });

  return (
    <Cell
      flex
      className={className}
      style={style}
      padding="15px"
      width={table.widths.status}
    >
      {!notification.read && (
        <span
          className={`${color} absolute left-[-20px] rounded-full h-[10px] w-[10px] mr-[10px]`}
        />
      )}
      <Typography truncate weight={weight}>
        {label}
      </Typography>
    </Cell>
  );
};

export const NotificationStatus = (props: StatusProps) => {
  const { className, notification, style, weight } = props;
  const { t } = useTranslation();
  const { metadata } = useNotificationsPage();
  const { color, label } = getStatus({ metadata, notification, t });

  return (
    <div className={`${className} flex gap-[10px]`} style={style}>
      <span className={`${color} rounded-full h-[10px] w-[10px]`} />
      <Typography truncate weight={weight}>
        {label}
      </Typography>
    </div>
  );
};
